// Added by Sejune Oh on 2020-03-02
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { FC, createContext, useState, useCallback } from 'react'
import { SnackbarProvider } from 'notistack'

// UI
import NotiStack, { NotiStackOptionProps } from './NotiStack'
import NotiPopup from '../NotificationsItem'

// Constants
import { MAX_NOTI_COUNT, NOTI_AUTO_HIDE_DURATION, KeyIndexedObject } from '@/helper/define'
import { FROM_NOTI_CENTER } from '@/helper/define/notificationsDefine'

// Context interface
type NotiStackContextAnchorOriginVertcal = 'top' | 'bottom'
type NotiStackContextAnchorOriginHorizontal = 'left' | 'center' | 'right'
interface NotiStackContextProps {
  changeMaxNotiCount: (count: number) => void
  changeAutoHideDuration: (duration: number) => void
  changeAnchorOrigin: (
    vertical: NotiStackContextAnchorOriginVertcal,
    horizontal: NotiStackContextAnchorOriginHorizontal
  ) => void
  changePreventDuplicateStatus: (status: boolean) => void
  setNotiInfo: (id: string, options?: NotiStackOptionProps) => void
  unsetNotiInfo: (id: string) => void
  getNotiInfo: (id: string) => any
}

interface NotiStackProviderProps {
  children: any
}

// Context
export const NotiStackContext = createContext<NotiStackContextProps | null>(null)

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const NotiStackProvider: FC<NotiStackProviderProps> = ({ children, ...props }) => {
  const [maxNotiCount, setMaxNotiCount] = useState(MAX_NOTI_COUNT)
  const [autoHideDuration, setAutoHideDuration] = useState(NOTI_AUTO_HIDE_DURATION)
  const [anchorOrigin, setAnchorOrigin] = useState<{
    vertical: NotiStackContextAnchorOriginVertcal
    horizontal: NotiStackContextAnchorOriginHorizontal
  }>({ vertical: 'bottom', horizontal: 'right' })
  const [preventDuplicate, setPreventDuplicateStatus] = useState(true)
  const [notiInfos, setNotiInfos] = useState<KeyIndexedObject<NotiStackOptionProps>>({})

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Functions
  /**
   * @brief maximum notice의 수를 변경.
   * @param count [number]
   */
  const changeMaxNotiCount = (count: number) => setMaxNotiCount(count)

  /**
   * @brief notice가 자동으로 닫히는 시간을 변경.
   * @param duration [number]
   */
  const changeAutoHideDuration = (duration: number) => setAutoHideDuration(duration)

  /**
   * @brief notice의 위치를 변경.
   * @param vertical ["top" | "bottom"]
   * @param horizontal ["left" | "center" | "right"]
   */
  const changeAnchorOrigin = (
    vertical: NotiStackContextAnchorOriginVertcal,
    horizontal: NotiStackContextAnchorOriginHorizontal
  ) => setAnchorOrigin({ vertical, horizontal })

  /**
   * @brief Key가 중복되는 notice를 보이지 않도록 하는 플래그를 변경.
   * @param status [boolean]
   */
  const changePreventDuplicateStatus = (status: boolean) => setPreventDuplicateStatus(status)

  /**
   * @brief Notice의 추가 정보를 설정함.
   * @param id [string]
   * @param options [NotiStackOptionProps]
   */
  const setNotiInfo = useCallback(
    (id: string, options?: NotiStackOptionProps) => {
      if (!options) return

      setNotiInfos({
        ...notiInfos,
        [id]: { ...options },
      })
    },
    [notiInfos]
  )

  /**
   * @brief notice의 추가정보를 가지고 오기 위한 getter
   * @param id [string]
   */
  const unsetNotiInfo = useCallback(
    (id: string) => {
      if (!notiInfos[id]) return

      const updatedNoticeInfos = { ...notiInfos }
      delete updatedNoticeInfos[id]
      setNotiInfos({ ...updatedNoticeInfos })
    },
    [notiInfos]
  )

  /**
   * @breief notice의 추가정보를 가지고 오기 위한 getter
   * @param id [string]
   */
  const getNotiInfo = useCallback(
    (id: string) => {
      return notiInfos[id]
    },
    [notiInfos]
  )

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Context initial value
  const notiStackContext = {
    changeMaxNotiCount,
    changeAutoHideDuration,
    changeAnchorOrigin,
    changePreventDuplicateStatus,
    setNotiInfo,
    unsetNotiInfo,
    getNotiInfo,
  }

  const getSnackbarContent = (key: React.ReactText, message: React.ReactNode) => {
    const stringifiedKey = key as string
    const notificationId = stringifiedKey.split('_')[1]
    if (stringifiedKey.includes(FROM_NOTI_CENTER)) return <NotiPopup id={key} message={message} notificationId={notificationId} isPopUp />
    return <NotiStack id={key} message={message} />
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Render
  return (
    <NotiStackContext.Provider value={notiStackContext}>
      <SnackbarProvider
        maxSnack={maxNotiCount}
        anchorOrigin={anchorOrigin}
        preventDuplicate={preventDuplicate}
        content={getSnackbarContent}
        onClose={(e, reason, key) => unsetNotiInfo(key as string)}
        autoHideDuration={autoHideDuration}
      >
        {children}
      </SnackbarProvider>
    </NotiStackContext.Provider>
  )
}

export default NotiStackProvider
